import { Card, CardContent, Typography, Box, Tooltip } from '@mui/material'
import { alpha, keyframes, useTheme } from '@mui/material/styles'
import { tokens } from '../../../theme'

const countUp = keyframes`
  from { opacity: 0; transform: translateY(10px); }
  to { opacity: 1; transform: translateY(0); }
`

/**
 * ContextCard — Value positioned against a benchmark scale
 * Use for metrics that gain meaning from context (e.g., efficiency vs industry avg)
 */
const ContextCard = ({
  title,
  value,
  displayValue,
  uncertainty,
  benchmarkMin = 0,
  benchmarkMax = 100,
  benchmarkLabel,
  benchmarkValue,
  subtitle,
  icon,
  color = '#A0CE4E',
  unit = '%',
  onClick
}) => {
  const theme = useTheme()
  const palette = theme.palette

  // Normalize value position on scale (0-100%)
  const range = benchmarkMax - benchmarkMin
  const position = range > 0 ? Math.min(100, Math.max(0, ((value - benchmarkMin) / range) * 100)) : 50
  const benchmarkPos = benchmarkValue != null && range > 0
    ? Math.min(100, Math.max(0, ((benchmarkValue - benchmarkMin) / range) * 100))
    : null

  return (
    <Card
      sx={{
        height: '100%',
        cursor: onClick ? 'pointer' : 'default',
        transition: `all ${tokens.transitions.normal}`,
        position: 'relative',
        overflow: 'hidden',
        bgcolor: palette.background.paper,
        border: `1px solid ${palette.divider}`,
        '&:hover': onClick ? {
          transform: 'translateY(-2px)',
          borderColor: alpha(color, 0.3),
          boxShadow: `0 4px 16px rgba(0,0,0,0.15), 0 0 20px ${alpha(color, 0.1)}`
        } : {
          borderColor: alpha(palette.text.primary, 0.1)
        },
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          height: '2px',
          background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.4)} 100%)`,
          opacity: 0.8
        }
      }}
      onClick={onClick}
    >
      <CardContent sx={{ p: { xs: 2, sm: 2.5 }, pb: { xs: '16px !important', sm: '20px !important' } }}>
        <Box sx={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between' }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Title */}
            <Typography
              variant="overline"
              sx={{
                color: 'text.secondary',
                fontWeight: 600,
                fontSize: '0.7rem',
                letterSpacing: '0.1em',
                lineHeight: 1.5,
                display: 'block',
                mb: 0.75
              }}
            >
              {title}
            </Typography>

            {/* Value with optional uncertainty */}
            <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 1, flexWrap: 'wrap' }}>
              <Typography
                sx={{
                  fontFamily: '"JetBrains Mono", monospace',
                  fontWeight: 700,
                  fontSize: { xs: '1.5rem', sm: '1.75rem', md: '2rem' },
                  color: 'text.primary',
                  lineHeight: 1,
                  letterSpacing: '-0.02em',
                  animation: `${countUp} 0.5s ease-out`
                }}
              >
                {displayValue || `${value}${unit}`}
              </Typography>
              {uncertainty && (
                <Tooltip
                  title="Standard deviation (1σ) — represents typical variation from mean"
                  arrow
                  placement="top"
                >
                  <Typography
                    component="span"
                    sx={{
                      color: 'text.secondary',
                      fontFamily: '"JetBrains Mono", monospace',
                      fontWeight: 500,
                      fontSize: '0.75rem',
                      cursor: 'help',
                      borderBottom: '1px dotted',
                      borderColor: 'text.disabled'
                    }}
                  >
                    {uncertainty}
                  </Typography>
                </Tooltip>
              )}
            </Box>

            {/* Benchmark scale */}
            <Box sx={{ mt: 1.5, pr: 1 }}>
              <Box
                sx={{
                  position: 'relative',
                  height: 6,
                  bgcolor: alpha(palette.text.primary, 0.06),
                  borderRadius: 3
                }}
              >
                {/* Filled portion */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    height: '100%',
                    width: `${position}%`,
                    bgcolor: alpha(color, 0.4),
                    borderRadius: 3,
                    transition: 'width 0.8s ease-out'
                  }}
                />
                {/* Value marker */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: -3,
                    left: `${position}%`,
                    transform: 'translateX(-50%)',
                    width: 12,
                    height: 12,
                    borderRadius: '50%',
                    bgcolor: color,
                    border: `2px solid ${palette.background.paper}`,
                    boxShadow: `0 0 8px ${alpha(color, 0.4)}`,
                    transition: 'left 0.8s ease-out'
                  }}
                />
                {/* Benchmark marker */}
                {benchmarkPos != null && (
                  <Tooltip title={benchmarkLabel || `Benchmark: ${benchmarkValue}${unit}`} arrow>
                    <Box
                      sx={{
                        position: 'absolute',
                        top: -2,
                        left: `${benchmarkPos}%`,
                        transform: 'translateX(-50%)',
                        width: 2,
                        height: 10,
                        bgcolor: palette.text.disabled,
                        cursor: 'help',
                        '&::after': {
                          content: '""',
                          position: 'absolute',
                          top: -2,
                          left: -3,
                          width: 8,
                          height: 14,
                          // invisible hit area
                        }
                      }}
                    />
                  </Tooltip>
                )}
              </Box>
              {/* Scale labels */}
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 0.5 }}>
                <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
                  {benchmarkMin}{unit}
                </Typography>
                {benchmarkLabel && benchmarkPos != null && (
                  <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled', fontWeight: 600 }}>
                    {benchmarkLabel}
                  </Typography>
                )}
                <Typography sx={{ fontSize: '0.55rem', color: 'text.disabled' }}>
                  {benchmarkMax}{unit}
                </Typography>
              </Box>
            </Box>

            {/* Subtitle */}
            <Typography
              variant="caption"
              sx={{
                color: 'text.secondary',
                display: 'block',
                mt: 0.75,
                fontSize: '0.75rem',
                lineHeight: 1.4
              }}
            >
              {subtitle}
            </Typography>
          </Box>

          {/* Icon Badge */}
          {icon && (
            <Box
              sx={{
                background: `linear-gradient(135deg, ${color} 0%, ${alpha(color, 0.7)} 100%)`,
                borderRadius: tokens.radius.sm,
                p: { xs: 0.75, sm: 1, md: 1.25 },
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                boxShadow: `0 4px 16px ${alpha(color, 0.3)}`,
                transition: `all ${tokens.transitions.normal}`,
                ml: { xs: 1, sm: 1.5 },
                flexShrink: 0,
                '& .MuiSvgIcon-root': {
                  color: palette.primary.contrastText,
                  fontSize: { xs: 18, sm: 20, md: 24 }
                }
              }}
            >
              {icon}
            </Box>
          )}
        </Box>
      </CardContent>
    </Card>
  )
}

export default ContextCard
